<template>
  <div class="h-full flex flex-col mt-4 md:mt-9 min-h-0">
    <div id="new-modifier" class="grid grid-cols-2 md:grid-cols-6 gap-x-4 gap-y-0 md:gap-4 mb-4">
      <l-field :label="t('ordering.name')" class="md:col-span-3">
        <l-input
          v-model="openModifier.name"
          size="small"
          :placeholder="t('ordering.name')"
          :wrong-value="showError"
        />
      </l-field>
      <l-field :label="t('ordering.price')" class="md:col-span-2">
        <l-number-input
          v-model="openModifier.priceImpact"
          format="currency"
          size="small"
          :min="0"
          :placeholder="t('ordering.price')"
          :icon="currencyIcon"
        />
      </l-field>
      <l-button
        size="small"
        :type="breakpoints.md.value ? 'primary' : 'secondary'"
        class="col-span-2 md:col-span-1 md:self-end md:mb-4"
        @click="createModifier()"
      >
        {{ t('ordering.create') }}
      </l-button>
    </div>
    <div class="h-full scrolling-touch overflow-scroll min-h-0">
      <div class="grid md:grid-cols-2 sm:grid-cols-1 gap-4">
        <div
          v-for="modifier in openModifiers"
          :key="modifier.id"
          class="rounded border border-v-300 bg-n-600 flex items-center overflow-hidden gap-2 w-full md:h-16 h-12 px-2"
          @click="$emit('delete', modifier.id)"
        >
          <div class="flex items-center justify-between w-full">
            <div class="text-n-0 font-body text-base">
              {{ modifier.name }}
            </div>
            <div v-if="modifier.priceImpact > 0" class="text-sm text-v-300">
              {{ currency(modifier.priceImpact) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LField, LInput, LNumberInput } from '@last/core-ui/paprika'

import { useMoney } from '@/composables/useMoney'
import { managerConfirmation } from '@/plugins/managerConfirmation'
import { useConfigStore } from '@/store/config'

const breakpoints = useBreakpoints(breakpointsTailwind)

const { openModifiers } = defineProps<{
  openModifiers: { id: string; name: string; priceImpact: number; quantity: number }[]
}>()

const emit = defineEmits<{
  delete: [string]
  create: [{ name: string; priceImpact: number }]
}>()

const { currency } = useMoney()

const openModifier = ref({
  name: null,
  priceImpact: null,
  quantity: 1
})
const showError = ref(false)

const configStore = useConfigStore()
const { t } = useI18n()
const { currencyIcon } = storeToRefs(configStore)

watch(
  () => openModifier.value.name,
  () => {
    if (showError.value && openModifier.value.name) {
      showError.value = false
    }
  }
)

async function createModifier() {
  showError.value = false
  if (await managerConfirmation('OPEN_PRODUCTS_CREATOR')) {
    const value = openModifier.value
    if (!value.name) {
      showError.value = true
      return
    }
    emit('create', value)
    openModifier.value = { name: null, priceImpact: null, quantity: 1 }
  }
}
</script>
